/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 3rd 2022, 8:50:06 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import InputIcon from "@mui/icons-material/Input";
import { DnDItemTypes } from "config";
import { useFieldTypesAPI } from "form-builder/services";
import { FormField, FIELD_TYPES } from "lib/schemas";
import { ToolBarItemButton, ToolBarItemDragLayer } from "common/components/toolbar";
import { COLORSCHEME } from "theme/styles";
import { getFieldTypeWidgets, getConfigurableFieldTypes } from "./FieldTypeWidgets";
import { GenericDragSource, CustomDragLayer, useOSCustomDragLayer } from "ui-widgets/drag-and-drop";

type FieldTypeButtonProps = Pick<FormField, "field_type"> & {
  title: React.ReactNode;
  iconComponent: React.ReactNode;
};

export interface IFieldDnDSelectorState {
  newFieldType: FIELD_TYPES;
}

const FieldTypeButton = ({ field_type, title, iconComponent }: FieldTypeButtonProps) => {
  return (
    <GenericDragSource<IFieldDnDSelectorState>
      itemType={DnDItemTypes.OS_ADD_NEW_FORM_FIELD}
      dragItemState={{ newFieldType: field_type }}
      hideDefaultDragPreview
    >
      <ToolBarItemButton
        tooltipText={title}
        buttonIcon={<div style={{ color: COLORSCHEME.secondary, fontSize: 25 }}>{iconComponent}</div>}
        buttonTitle={title}
      />
    </GenericDragSource>
  );
};

const CustomFieldTypeButtons = () => {
  const { useFetchCustomFieldTypes } = useFieldTypesAPI();
  const { customFieldTypes } = useFetchCustomFieldTypes();

  return (
    <>
      {Object.keys(customFieldTypes).map((field_type_code) => (
        <FieldTypeButton
          key={`${field_type_code}`}
          field_type={field_type_code as unknown as FIELD_TYPES}
          title={customFieldTypes[field_type_code]}
          iconComponent={<InputIcon />}
        />
      ))}
    </>
  );
};

export const AddNewFieldToolSet = () => {
  return (
    <>
      {getConfigurableFieldTypes().map((field_type_code) => {
        const [title, iconComponent] = getFieldTypeWidgets(field_type_code);
        return (
          <FieldTypeButton
            key={`${field_type_code}`}
            field_type={field_type_code as unknown as FIELD_TYPES}
            title={title}
            iconComponent={iconComponent}
          />
        );
      })}
      <CustomFieldTypeButtons />
    </>
  );
};

export const NewFieldDragLayer = React.memo(() => {
  const { collectedProps } = useOSCustomDragLayer<IFieldDnDSelectorState>({
    itemType: DnDItemTypes.OS_ADD_NEW_FORM_FIELD,
  });

  const newFieldType = collectedProps?.item?.newFieldType;

  const { useFetchCustomFieldTypes } = useFieldTypesAPI();
  const { customFieldTypes } = useFetchCustomFieldTypes();

  let buttonIcon: React.ReactNode = null;
  let buttonTitle: React.ReactNode = null;

  const customFieldTypeTitle = customFieldTypes[newFieldType];

  if (customFieldTypeTitle) {
    buttonIcon = <div style={{ color: COLORSCHEME.secondary, fontSize: 25 }}>{<InputIcon />}</div>;
    buttonTitle = customFieldTypeTitle;
  }

  if (!customFieldTypeTitle) {
    buttonIcon = (
      <div style={{ color: COLORSCHEME.secondary, fontSize: 25 }}>{getFieldTypeWidgets(newFieldType)?.[1]}</div>
    );
    buttonTitle = getFieldTypeWidgets(newFieldType)?.[0];
  }

  return newFieldType ? (
    <CustomDragLayer collectedProps={collectedProps as any}>
      <ToolBarItemDragLayer buttonIcon={buttonIcon} buttonTitle={buttonTitle} />
    </CustomDragLayer>
  ) : null;
});
